import { useCallback, useEffect, useMemo, useState } from "react";
import { DomainHealthRecord } from "../../../../domain/healthRecord";
import {
  buildHealthRecordVaccinationSource,
  reconcileEvcVaccinations,
  type EvcVaccinationMatch,
} from "./evcVaccinationReconciliation";
import { useDaphne } from "../../../hooks/useDaphne";
import { VaccinationToImport } from "./types";

export function useEvcVaccinationSelection({
  vaccinations,
  targetMode,
  selectedExistingHealthRecord,
}: {
  vaccinations: VaccinationToImport[];
  targetMode: "new" | "existing";
  selectedExistingHealthRecord?: DomainHealthRecord;
}) {
  const daphne = useDaphne();
  const [vaccinationSelectionById, setVaccinationSelectionById] = useState<Record<number, boolean>>({});
  const vaccinationIdsKey = useMemo(() => vaccinations.map((vaccination) => vaccination.id).join("|"), [vaccinations]);

  useEffect(() => {
    setVaccinationSelectionById({});
  }, [vaccinationIdsKey]);

  const healthRecordVaccinationsSource = useMemo(
    () =>
      targetMode === "existing"
        ? buildHealthRecordVaccinationSource(selectedExistingHealthRecord?.vaccinationActs, (vaccineId) =>
            daphne.repositories.vaccines.find(vaccineId),
          )
        : [],
    [daphne, selectedExistingHealthRecord?.vaccinationActs, targetMode],
  );

  const reconciledVaccinations = useMemo<EvcVaccinationMatch[]>(
    () => reconcileEvcVaccinations(vaccinations, healthRecordVaccinationsSource),
    [healthRecordVaccinationsSource, vaccinations],
  );

  const reconciledVaccinationById = useMemo(
    () => new Map(reconciledVaccinations.map((vaccination) => [vaccination.evcVaccinationId, vaccination])),
    [reconciledVaccinations],
  );

  const matchStats = useMemo(() => {
    const exact = reconciledVaccinations.filter((vaccination) => vaccination.status === "exact").length;
    const fresh = reconciledVaccinations.filter((vaccination) => vaccination.status === "new").length;
    return { exact, fresh, total: reconciledVaccinations.length };
  }, [reconciledVaccinations]);

  const selectedVaccinationIds = useMemo(
    () => getSelectedVaccinationIds(vaccinations, targetMode, vaccinationSelectionById, reconciledVaccinationById),
    [reconciledVaccinationById, targetMode, vaccinationSelectionById, vaccinations],
  );

  const toggleVaccination = useCallback(
    (id: number) => {
      setVaccinationSelectionById((previous) =>
        toggleVaccinationSelection(id, selectedVaccinationIds.has(id), targetMode, reconciledVaccinationById, previous),
      );
    },
    [reconciledVaccinationById, selectedVaccinationIds, targetMode],
  );

  return {
    matchStats,
    reconciledVaccinationById,
    selectedVaccinationIds,
    selectedCount: selectedVaccinationIds.size,
    toggleVaccination,
  };
}

function getSelectedVaccinationIds(
  vaccinations: VaccinationToImport[],
  targetMode: "new" | "existing",
  selectionById: Record<number, boolean>,
  matchById: Map<number, EvcVaccinationMatch>,
): Set<number> {
  return new Set(
    vaccinations
      .filter((vaccination) => isVaccinationSelected(vaccination.id, targetMode, selectionById, matchById))
      .map((vaccination) => vaccination.id),
  );
}

function isVaccinationSelected(
  vaccinationId: number,
  targetMode: "new" | "existing",
  selectionById: Record<number, boolean>,
  matchById: Map<number, EvcVaccinationMatch>,
): boolean {
  const match = matchById.get(vaccinationId);
  if (targetMode === "existing" && match?.status === "exact") {
    return false;
  }

  return selectionById[vaccinationId] ?? true;
}

function toggleVaccinationSelection(
  vaccinationId: number,
  selected: boolean,
  targetMode: "new" | "existing",
  matchById: Map<number, EvcVaccinationMatch>,
  selectionById: Record<number, boolean>,
): Record<number, boolean> {
  const match = matchById.get(vaccinationId);
  if (targetMode === "existing" && match?.status === "exact") {
    return selectionById;
  }

  return {
    ...selectionById,
    [vaccinationId]: !selected,
  };
}
